import { QUESTION_SINGLE } from 'data/nodeTypes';
import {
  Matcher,
  Operator,
  QuotaConditionTypeEnum,
  Rule,
  Statement,
  conditionService,
  ruleService,
} from 'utils/adl-services';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
import { MIN_SCREENERS_ANSWER_LIMIT } from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/constants';
import { isAnswerEmpty } from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/utils';

import {
  buildScreenOutOutcome,
  getLeafConditions,
  isScreenOutStatement,
  makeConditionReuse,
  matchPreviousStatements,
} from './rules';

const { ALLOWED, NOT_ALLOWED, STRICT_NOT_ALLOWED } = SCREENING_ALLOWED_VALUES;

// Single selection offers no not allowed: it behaves the same as strict there.
export const getOfferedScreenerTypes = (type) =>
  type === QUESTION_SINGLE
    ? [ALLOWED, STRICT_NOT_ALLOWED]
    : [ALLOWED, NOT_ALLOWED, STRICT_NOT_ALLOWED];

const answerRefIdsOf = (node) =>
  (node.answers || []).filter(Boolean).map(({ refId }) => refId);

// Single: a screen-out statement per strict not allowed answer; no statement
// means allowed.
// Multiple: one combined statement `Any(Any(Equal strict…), All(NotEqual allowed…))`,
// or just one of the two groups; an answer absent from it is not allowed.
// Both read the leaf matchers, not the structure, so legacy and collapsed
// shapes decode too.
export function decodeSingleMultiple({ node, statements = [] }) {
  const typeByMatcher = {
    [Matcher.Equal]: STRICT_NOT_ALLOWED,
    [Matcher.NotEqual]: ALLOWED,
  };
  const decoded = {};
  statements
    .filter(isScreenOutStatement)
    .flatMap(({ rule }) => getLeafConditions(rule))
    .forEach(({ matcher, value }) => {
      if (typeByMatcher[matcher]) decoded[value] = typeByMatcher[matcher];
    });

  const absent = node.type === QUESTION_SINGLE ? ALLOWED : NOT_ALLOWED;
  return answerRefIdsOf(node).reduce((acc, answerRefId) => {
    acc[answerRefId] = decoded[answerRefId] || absent;
    return acc;
  }, {});
}

const answersOfType = (node, screenerTypes, screenerType) =>
  answerRefIdsOf(node).filter(
    (answerRefId) => screenerTypes[answerRefId] === screenerType,
  );

const singleConditionValue = ({ rule }) => {
  const conditions = getLeafConditions(rule);
  return conditions.length === 1 ? conditions[0].value : undefined;
};

// Encodes the screen-out statements, reusing the ids of `previousStatements`.
export function encodeSingleMultiple({
  node,
  screenerTypes,
  previousStatements = [],
}) {
  const questionRefId = node.refId;
  const reuseCondition = makeConditionReuse(previousStatements);
  const answerCondition = (matcher, createCondition) => (answerRefId) =>
    reuseCondition({
      matcher,
      value: answerRefId,
      create: () => createCondition(questionRefId, answerRefId),
    });
  const equal = answerCondition(Matcher.Equal, (...args) =>
    conditionService.createAnswerConditionEqual(...args),
  );
  const notEqual = answerCondition(Matcher.NotEqual, (...args) =>
    conditionService.createAnswerConditionNotEqual(...args),
  );
  const statementFrom = (previous, buildRule) =>
    new Statement({
      id: previous?.id,
      rule: buildRule(previous?.rule),
      outcome: previous?.outcome || buildScreenOutOutcome(),
    });

  const strict = answersOfType(node, screenerTypes, STRICT_NOT_ALLOWED);

  if (node.type === QUESTION_SINGLE) {
    const previous = matchPreviousStatements({
      previousStatements,
      keys: strict,
      keyOf: singleConditionValue,
    });
    return strict.map((answerRefId, index) =>
      statementFrom(
        previous[index],
        (previousRule) =>
          new Rule({
            id: previousRule?.id,
            operator: Operator.Any,
            conditions: [equal(answerRefId)],
          }),
      ),
    );
  }

  const allowed = answersOfType(node, screenerTypes, ALLOWED);
  if (strict.length === 0 && allowed.length === 0) return [];

  // One combined statement: it takes over the first previous statement's ids.
  // The strict conditions get a group of their own: ADL walks the rules with
  // `getAllLeafRules`, which throws on a rule mixing conditions and rules.
  const [previous] = previousStatements;
  return [
    statementFrom(previous, (previousRule) => {
      const previousIsCombined =
        Boolean(previousRule) && !ruleService.isLeafRule(previousRule);
      const previousGroups = previousIsCombined
        ? previousRule.conditions.filter((condition) =>
            ruleService.isRule(condition),
          )
        : [previousRule].filter(Boolean);
      const previousGroup = (operator) =>
        previousGroups.find((group) => group.operator === operator);
      const group = (operator, conditions) =>
        new Rule({ id: previousGroup(operator)?.id, operator, conditions });

      if (allowed.length === 0) return group(Operator.Any, strict.map(equal));
      if (strict.length === 0)
        return group(Operator.All, allowed.map(notEqual));

      return new Rule({
        id: previousIsCombined ? previousRule.id : undefined,
        operator: Operator.Any,
        conditions: [
          group(Operator.Any, strict.map(equal)),
          group(Operator.All, allowed.map(notEqual)),
        ],
      });
    }),
  ];
}

/**
 * The screening errors of a single or multiple selection question: too few
 * filled answers, no allowed answer, or (while the screener is active) no not
 * allowed or strict not allowed answer. Only filled answers count; the first
 * error found is the one reported.
 */
export function getScreeningErrors({
  question,
  screenerTypes,
  isScreenerActive,
}) {
  const filledTypes = (question.answers || [])
    .filter((answer) => answer && !isAnswerEmpty(answer))
    .map(({ refId }) => screenerTypes[refId]);

  if (filledTypes.length < MIN_SCREENERS_ANSWER_LIMIT) {
    return { missingScreeningAnswers: true };
  }
  if (!filledTypes.includes(ALLOWED)) {
    return { missingAllowedAnswer: true };
  }
  if (
    isScreenerActive &&
    !filledTypes.some(
      (type) => type === NOT_ALLOWED || type === STRICT_NOT_ALLOWED,
    )
  ) {
    return { missingNotAllowedAnswer: true };
  }
  return {};
}

const withSimpleQuota = (statement) => {
  const quota = statement.rule?.quota;
  if (!quota || quota.context?.type !== undefined) return statement;

  return {
    ...statement,
    rule: {
      ...statement.rule,
      quota: {
        ...quota,
        context: { ...quota.context, type: QuotaConditionTypeEnum.Simple },
      },
    },
  };
};

// Load-time normalization of screening nodes: legacy screening quotas with no
// context type become `Simple`. Legacy allow statements (success statements
// with a rule) are kept as they are: they become navigation statements.
export function normalizeScreeningNavigation(ql) {
  const screeningNodeIds = new Set(
    (ql?.questionnaire?.nodes || [])
      .filter(({ isScreening }) => isScreening)
      .map(({ id }) => id),
  );
  if (screeningNodeIds.size === 0) return ql;

  let changed = false;
  const nodeStatements = ql.nodeStatements.map((nodeStatement) => {
    if (!screeningNodeIds.has(nodeStatement.nodeId)) return nodeStatement;

    const statements = nodeStatement.statements.map(withSimpleQuota);
    const nodeChanged = statements.some(
      (statement, index) => statement !== nodeStatement.statements[index],
    );
    if (!nodeChanged) return nodeStatement;

    changed = true;
    return { ...nodeStatement, statements };
  });

  return changed ? { ...ql, nodeStatements } : ql;
}
